@if (svc.jobs().length > 0) {
  <div class="tradex-queue-boundary pointer-events-none fixed inset-3 z-[999]">
    <div cdkDrag cdkDragBoundary=".tradex-queue-boundary" class="pointer-events-auto absolute bottom-0 right-0 flex max-w-full flex-col items-end">
      <!-- Collapsed pill -->
      @if (!expanded()) {
        <div class="queue-purple flex items-center rounded-full px-2 text-white shadow-xl">
          <span cdkDragHandle class="flex cursor-grab items-center p-2 active:cursor-grabbing" matTooltip="Drag to move queue">
            <mat-icon class="!text-white/80">drag_indicator</mat-icon>
          </span>
          <button
            (click)="expanded.set(true)"
            aria-label="Expand import queue"
            class="flex items-center gap-2 rounded-full px-3 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 focus-visible:bg-white/10"
          >
            @if (svc.hasActive()) {
              <mat-spinner diameter="14" class="opacity-90"></mat-spinner>
            } @else {
              <mat-icon class="!h-4 !w-4 !text-[1rem]">check_circle</mat-icon>
            }
            {{ pillLabel() }}
            <mat-icon class="!h-4 !w-4 !text-[0.875rem] opacity-60">keyboard_arrow_up</mat-icon>
          </button>
        </div>
      }

      <!-- Expanded panel -->
      @if (expanded()) {
        <div
          class="flex max-h-[calc(100dvh-1.5rem)] w-[29rem] max-w-full flex-col overflow-hidden rounded-2xl border border-indigo-200 bg-white shadow-2xl dark:border-indigo-900 dark:bg-slate-900"
        >
          <!-- Header -->
          <div class="queue-purple flex shrink-0 items-center justify-between gap-2 px-4 py-3">
            <div cdkDragHandle class="flex min-w-0 flex-1 cursor-grab items-center gap-2 active:cursor-grabbing" matTooltip="Drag to move queue">
              <mat-icon class="shrink-0 !text-white/70">drag_indicator</mat-icon>
              @if (svc.hasActive()) {
                <mat-spinner diameter="16" class="opacity-90"></mat-spinner>
              } @else {
                <mat-icon class="!h-5 !w-5 !text-[1.125rem] text-white">task_alt</mat-icon>
              }
              <span class="text-base font-semibold text-white">TradeX Import Queue</span>
            </div>
            <div class="flex items-center gap-1">
              <button
                mat-icon-button
                class="!bg-white/15 shrink-0 !rounded-full !text-white transition-colors hover:!bg-white/30 focus-visible:!bg-white/30"
                aria-label="Minimize import queue"
                matTooltip="Minimize queue"
                (click)="expanded.set(false)"
              >
                <mat-icon class="!text-white">keyboard_arrow_down</mat-icon>
              </button>
            </div>
          </div>

          <!-- Jobs -->
          <div class="max-h-[30rem] min-h-0 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
            @for (job of svc.jobs(); track job.jobId) {
              <div class="job-card px-4 py-3.5">
                <!-- Top row: badge + elapsed + dismiss -->
                <div class="mb-2 flex items-center justify-between gap-2">
                  <div class="flex items-center gap-2">
                    <!-- Status icon -->
                    @if (job.status === 'QUEUED') {
                      <div class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-dashed border-slate-300"></div>
                    } @else if (job.status === 'PROCESSING') {
                      <mat-spinner diameter="16" color="primary" class="shrink-0"></mat-spinner>
                    } @else if (job.status === 'DONE') {
                      <div class="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-green-500">
                        <mat-icon class="!h-3 !w-3 !text-[0.625rem] text-white">check</mat-icon>
                      </div>
                    } @else {
                      <div class="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-500">
                        <mat-icon class="!h-3 !w-3 !text-[0.625rem] text-white">close</mat-icon>
                      </div>
                    }
                    <span class="text-xs font-bold uppercase tracking-wide" [class]="badgeClass(job.status)">
                      {{ statusBadge(job.status) }}
                    </span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">{{ fileCountLabel(job) }}</span>
                  </div>
                  <div class="flex shrink-0 items-center gap-1">
                    @if ((job.status === 'PROCESSING' || job.status === 'DONE') && (job.elapsedMs ?? 0) > 0) {
                      <span class="text-xs tabular-nums text-slate-500 dark:text-slate-400">{{ elapsed(job) }}</span>
                    }
                    @if (job.status === 'ERROR') {
                      <button mat-icon-button class="!h-6 !w-6" (click)="svc.retryJob(job.jobId)" matTooltip="Retry — re-runs pipeline on same files">
                        <mat-icon class="!text-[0.875rem] text-indigo-500 hover:text-indigo-700">refresh</mat-icon>
                      </button>
                    }
                    @if (job.status !== 'DONE') {
                      <button
                        mat-icon-button
                        class="!h-6 !w-6"
                        (click)="svc.dismissJob(job.jobId)"
                        [matTooltip]="job.status === 'QUEUED' || job.status === 'PROCESSING' ? 'Force dismiss' : 'Dismiss'"
                      >
                        <mat-icon class="!text-[0.8125rem] text-slate-400 hover:text-slate-600">close</mat-icon>
                      </button>
                    }
                  </div>
                </div>

                <!-- Pipeline step indicator (active jobs only) -->
                @if (job.status === 'QUEUED' || job.status === 'PROCESSING') {
                  <div class="mb-2.5 flex items-center gap-0">
                    @for (step of steps; track step.label; let i = $index; let last = $last) {
                      <div class="flex min-w-0 flex-1 flex-col items-center">
                        <div class="flex h-6 w-6 items-center justify-center rounded-full text-[0.625rem] transition-all" [class]="stepClass(job, i)">
                          <mat-icon class="!h-3.5 !w-3.5 !text-[0.75rem]">{{ step.icon }}</mat-icon>
                        </div>
                        <span class="mt-1 text-xs font-medium" [class]="stepLabelClass(job, i)">{{ step.label }}</span>
                      </div>
                      @if (!last) {
                        <div class="step-line mb-3 transition-colors" [class]="stepLineClass(job, i)"></div>
                      }
                    }
                  </div>
                }

                <!-- Stage message -->
                <p class="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                  {{ stageText(job) }}
                </p>

                <!-- Error detail -->
                @if (job.status === 'ERROR') {
                  <p class="mt-1 line-clamp-2 text-xs leading-relaxed text-red-600 dark:text-red-400">
                    {{ job.errorMessage || 'An unexpected error occurred.' }}
                  </p>
                }

                <!-- Done action -->
                @if (job.status === 'DONE') {
                  <div class="mt-3 flex items-center justify-between gap-3">
                    @if (job.certId || job.targetCertId) {
                      <button
                        class="flex items-center gap-1 rounded-lg px-2 py-2 text-sm font-semibold text-indigo-600 transition-colors hover:bg-indigo-50 focus-visible:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950/40 dark:focus-visible:bg-indigo-950/40"
                        (click)="svc.requestOpenCert((job.certId ?? job.targetCertId)!)"
                      >
                        <mat-icon class="!h-3.5 !w-3.5 !text-[0.8125rem]">open_in_new</mat-icon>
                        {{ job.targetCertId ? 'Open updated certificate' : 'Open certificate' }}
                      </button>
                    }
                    <button
                      mat-button
                      class="ml-auto shrink-0 !rounded-lg !bg-indigo-50 !px-4 !text-indigo-700 transition-colors hover:!bg-indigo-100 focus-visible:!bg-indigo-100 dark:!bg-indigo-950/40 dark:!text-indigo-200 dark:hover:!bg-indigo-900/60 dark:focus-visible:!bg-indigo-900/60"
                      (click)="svc.dismissJob(job.jobId)"
                      matTooltip="Close completed import"
                    >
                      Close
                    </button>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>
  </div>
}
